/**
 * AUTH GUARD UTILITY
 * 
 * Prevents Firestore queries from running without valid authentication.
 * This prevents crashes and invalid queries.
 */

import { auth } from '../firebaseConfig';

/**
 * Check if user is authenticated and userId is valid
 * @param userId - The user ID to validate
 * @returns true if auth is valid, false otherwise
 */
export const isValidAuth = (userId?: string | null): boolean => {
  // Check Firebase Auth has current user
  const firebaseUser = auth.currentUser;
  if (!firebaseUser || !firebaseUser.uid) {
    if (import.meta.env.DEV) {
      console.warn('[AUTH_GUARD] No authenticated user');
    }
    return false;
  }

  // Check userId is provided and valid
  if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
    if (import.meta.env.DEV) {
      console.warn('[AUTH_GUARD] Invalid userId:', userId);
    }
    return false;
  }

  // Optional: Verify userId matches auth user (for security)
  // Uncomment if you want strict matching:
  // if (userId !== firebaseUser.uid) {
  //   if (import.meta.env.DEV) {
  //     console.warn('[AUTH_GUARD] userId mismatch:', { userId, authUid: firebaseUser.uid });
  //   }
  //   return false;
  // }

  return true;
};

/**
 * Guard wrapper for Firestore operations
 * Returns early if auth is invalid
 * @param userId - The user ID to validate
 * @param operation - Function to execute if auth is valid
 * @returns Result of operation or null if auth invalid
 */
export const withAuthGuard = <T>(
  userId: string | null | undefined,
  operation: () => T | Promise<T>
): T | Promise<T> | null => {
  if (!isValidAuth(userId)) {
    if (import.meta.env.DEV) {
      console.warn('[AUTH_GUARD] Blocking operation - invalid auth');
    }
    return null;
  }
  return operation();
};

